import { useEffect, useRef, useState } from "react";
import Topbar from "@/components/Topbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { HistoryDetailDialog } from "@/components/HistoryDetailDialog";
import {
  Check, X, Factory, ListChecks, Plus, Trash2, Edit, MapPin,
  Image as ImageIcon, Download, Package, Truck, Warehouse, AlertTriangle, FileText,
  Save, Cog, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { idSuffix } from "@/lib/idLabel";
import { formatDate, formatDateTime, formatTime } from "@/lib/dates";
import { fmtMB } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const DEST_DIR_TYPES = ["Recycler", "Converter", "Incinerator", "Landfill", "Exporter"];

function AgentDirectoryPanel() {
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", phone: "", ic_number: "" });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get("/agents").then(res => setAgents(res.data || [])).catch(() => {});
  }, []);

  async function saveAgent() {
    try {
      if (editId) {
        await api.put(`/agents/${editId}`, form);
        setAgents(prev => prev.map(a => a.id === editId ? { ...a, ...form } : a));
      } else {
        const res = await api.post("/agents", form);
        setAgents(prev => [...prev, res.data]);
      }
      setShowForm(false);
      setForm({ name: "", address: "", phone: "", ic_number: "" });
      setEditId(null);
      toast.success(editId ? "Agent updated" : "Agent added");
    } catch { toast.error("Failed to save agent"); }
  }

  async function deleteAgent(id) {
    try {
      await api.delete(`/agents/${id}`);
      setAgents(prev => prev.filter(a => a.id !== id));
      toast.success("Agent removed");
    } catch { toast.error("Failed to remove agent"); }
  }

  return (
    <div className="space-y-4" data-testid="agent-directory-panel">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-800">Registered collectors</h3>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ name: "", address: "", phone: "", ic_number: "" }); }}
          className="px-4 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-800"
          data-testid="agent-add-btn">
          + Add agent
        </button>
      </div>
      {agents.length === 0 && (
        <p className="text-sm text-gray-500">No agents registered yet. Add agents so collectors can select them when logging collections.</p>
      )}
      <div className="divide-y border rounded-xl overflow-hidden">
        {agents.map(a => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50" data-testid={`agent-row-${a.id}`}>
            <div>
              <p className="text-sm font-medium text-gray-800">{a.name}</p>
              <p className="text-xs text-gray-500">{[a.address, a.phone, a.ic_number].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditId(a.id); setForm({ name: a.name, address: a.address || "", phone: a.phone || "", ic_number: a.ic_number || "" }); setShowForm(true); }}
                className="text-xs text-blue-600 hover:underline" data-testid={`agent-edit-${a.id}`}>Edit</button>
              <button onClick={() => deleteAgent(a.id)}
                className="text-xs text-red-500 hover:underline" data-testid={`agent-delete-${a.id}`}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl space-y-3" data-testid="agent-form-modal">
            <h4 className="font-semibold text-gray-800">{editId ? "Edit agent" : "Add agent"}</h4>
            {[["name","Name *"],["address","Address"],["phone","Phone"],["ic_number","IC number"]].map(([k,label]) => (
              <div key={k}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input value={form[k]} onChange={e => setForm(p => ({...p,[k]:e.target.value}))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid={`agent-field-${k}`} />
              </div>
            ))}
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border rounded-lg" data-testid="agent-cancel-btn">Cancel</button>
              <button onClick={saveAgent} disabled={!form.name}
                className="px-4 py-2 text-sm bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50"
                data-testid="agent-save-btn">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Materials list a collector may have submitted under a single collection.
const MATERIAL_OPTIONS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];

// Compact, copy-able record identifier shown on every row / dialog so a
// support engineer or facility user can quickly reference an exact record
// without exposing the full UUID.
function RecordId({ id }) {
  if (!id) return null;
  return (
    <span
      className="text-[10px] font-mono text-[#3F3F3F]/40 select-all"
      title={id}
      data-testid={`record-id-${id}`}
    >
      ID: {String(id).slice(0, 8)}…
    </span>
  );
}

// We accept the new multi-row format `materials: [{material_type, weight_kg}]`
// and fall back to legacy single-value fields. Returns an array of
// { material_type, estimated_weight_kg, received_weight_kg } rows that the
// facility can edit at receipt time.
function buildReceiptMaterials(c) {
  if (!c) return [];
  const mats = Array.isArray(c.materials) ? c.materials : [];
  if (mats.length > 0) {
    return mats.map((m) => {
      const type = m.material_type || m.material || "";
      const est = Number(m.weight_kg) || 0;
      return {
        material_type: type,
        estimated_weight_kg: est,
        received_weight_kg: String(est || ""),
      };
    });
  }
  if (c.material_type) {
    const est = Number(c.estimated_weight_kg) || Number(c.total_weight_kg) || 0;
    return [{
      material_type: c.material_type,
      estimated_weight_kg: est,
      received_weight_kg: String(est || ""),
    }];
  }
  return [];
}

export default function FacilityDashboard() {
  const [collections, setCollections] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [processingRuns, setProcessingRuns] = useState([]);
  const [processingDrafts, setProcessingDrafts] = useState([]);
  const [shipmentDrafts, setShipmentDrafts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [collectionSubTab, setCollectionSubTab] = useState("pending");
  const [shipmentSubTab, setShipmentSubTab] = useState("pending");
  const [processingSubTab, setProcessingSubTab] = useState("submitted");
  const [detailMain, setDetailMain] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState("Received");
  const [confirmWeight, setConfirmWeight] = useState("");
  // Per-material edited rows for receipt: [{ material_type, estimated_weight_kg, received_weight_kg }]
  const [receiptMaterials, setReceiptMaterials] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Transaction inputs on receipt
  const [txAmount, setTxAmount] = useState("");
  const [txCurrency, setTxCurrency] = useState("RM");
  const [proofPath, setProofPath] = useState("");
  const [proofFilename, setProofFilename] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  const proofRef = useRef(null);
  const [receiptPhotos, setReceiptPhotos] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []);

  useLiveRefresh(load, { intervalMs: 15000 });

  async function load() {
    try {
      const [c, d, s, p, pd, sd] = await Promise.all([
        api.get("/collections"),
        api.get("/facility/dashboard"),
        api.get("/shipments"),
        api.get("/processing"),
        api.get("/processing/drafts").catch(() => ({ data: [] })),
        api.get("/shipments/drafts").catch(() => ({ data: [] })),
      ]);
      setCollections(c.data);
      setDashboard(d.data);
      setShipments(s.data || []);
      setProcessingRuns(Array.isArray(p.data) ? p.data : []);
      setProcessingDrafts(Array.isArray(pd.data) ? pd.data : []);
      setShipmentDrafts(Array.isArray(sd.data) ? sd.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  const pending = collections.filter((c) => c.status === "Pending");
  const received = collections.filter((c) => c.status === "Received");
  const rejected = collections.filter((c) => c.status === "Rejected");
  const pendingShipments  = shipments.filter((s) => (s.admin_status || "pending") === "pending");
  const approvedShipments = shipments.filter((s) => s.admin_status === "approved");
  const rejectedShipments = shipments.filter((s) => s.admin_status === "rejected");

  async function exportEvidence({ from, to }) {
    try {
      const params = {};
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      const res = await api.get("/facility/export.zip", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const fname = `facility_evidence_${(from || "all").slice(0, 10)}_${(to || "now").slice(0, 10)}.zip`;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Evidence package downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  // When opening receipt dialog, pre-fill transaction info from collector's input
  useEffect(() => {
    if (confirmTarget) {
      setTxAmount(
        confirmTarget?.transaction_amount != null
          ? String(confirmTarget?.transaction_amount)
          : ""
      );
      setTxCurrency(confirmTarget?.transaction_currency || "RM");
      setProofPath(confirmTarget?.payment_proof_path || "");
      setProofFilename(confirmTarget?.payment_proof_path ? "Already attached" : "");
    } else {
      setTxAmount("");
      setTxCurrency("RM");
      setProofPath("");
      setProofFilename("");
    }
  }, [confirmTarget]);

  async function uploadReceiptProof(file) {
    if (!file) return;
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("PDF, JPG, PNG or WebP only");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("File must be 12 MB or smaller");
      return;
    }
    setProofUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProofPath(data.storage_path);
      setProofFilename(file.name);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
    setProofUploading(false);
  }

  async function handleReceiptPhotoAdd(e) {
    const files = Array.from(e.target.files);
    const remaining = 3 - receiptPhotos.length;
    for (const file of files.slice(0, remaining)) {
      let timestamp = new Date().toISOString();
      let source = "upload";
      try {
        const exifr = (await import("exifr")).default;
        const exif = await exifr.parse(file, ["DateTimeOriginal"]);
        if (exif?.DateTimeOriginal) {
          timestamp = exif.DateTimeOriginal.toISOString();
          source = "exif";
        }
      } catch (err) {
        // EXIF parsing is best-effort — fall back to upload timestamp.
        console.debug("exifr parse failed", err);
      }
      const idx = Date.now() + Math.random();
      setReceiptPhotos(prev => [...prev, { idx, file, preview: URL.createObjectURL(file), timestamp, source, path: null, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const path = res.data?.storage_path || res.data?.path || "";
        setReceiptPhotos(prev => prev.map(p => p.idx === idx ? { ...p, path, uploading: false } : p));
      } catch {
        setReceiptPhotos(prev => prev.filter(p => p.idx !== idx));
        toast.error("Photo upload failed");
      }
    }
  }

  async function submitReceipt() {
    // Aggregate from the per-material rows.
    const validRows = (receiptMaterials || [])
      .filter((r) => r && r.material_type)
      .map((r) => ({
        material_type: r.material_type,
        weight_kg: parseFloat(r.received_weight_kg) || 0,
      }));
    const totalReceived = validRows.reduce((s, r) => s + r.weight_kg, 0);

    if (validRows.length === 0) {
      // Fallback to the legacy single-input flow.
      if (!confirmWeight || parseFloat(confirmWeight) < 0) {
        toast.error("Enter a valid received weight");
        return;
      }
    } else if (totalReceived < 0 || validRows.some((r) => r.weight_kg < 0)) {
      toast.error("Weights cannot be negative");
      return;
    }
    if (confirmAction === "Rejected" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    const body = {
      received_weight_kg: validRows.length > 0 ? totalReceived : parseFloat(confirmWeight),
      status_update: confirmAction,
      rejection_reason: confirmAction === "Rejected" ? rejectionReason.trim() : null,
      facility_photos: receiptPhotos.filter(p => p.path).map(p => ({ path: p.path, timestamp: p.timestamp, source: p.source })),
    };
    if (validRows.length > 0) body.materials = validRows;
    if (confirmAction === "Received") {
      const amt = parseFloat(txAmount);
      if (Number.isNaN(amt) || amt < 0) {
        toast.error("Transaction amount is required to approve a collection");
        return;
      }
      body.transaction_amount = amt;
      body.transaction_currency = "RM";
      // Payment proof is optional — only forward if the facility uploaded a new one
      if (proofPath && proofPath !== confirmTarget?.payment_proof_path) {
        body.payment_proof_path = proofPath;
      }
    }
    setBusy(true);
    try {
      const res = await api.post(`/collections/${confirmTarget?.id}/receipt`, body);
      toast.success(`Marked as ${confirmAction}`);
      if (res.data?.warnings?.length > 0) {
        res.data.warnings.forEach(w => toast(w, { style: { background: "#fef3c7", color: "#92400e" } }));
      }
      setConfirmTarget(null);
      setConfirmWeight("");
      setReceiptMaterials([]);
      setRejectionReason("");
      setReceiptPhotos([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Topbar subtitle="Facility" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="facility-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Facility console</div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-1">
              Incoming &amp; processing
            </h1>
            <p className="text-sm text-[#3F3F3F] mt-1">
              Confirm deliveries, then group received bales into a processing run.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate("/facility/processing")}
              data-testid="new-processing-btn"
              variant="outline"
              className="h-11 border-[#056839] text-[#056839] hover:bg-[#D2E4D6] rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New processing
            </Button>
            <Button
              onClick={() => navigate("/facility/shipment")}
              data-testid="new-shipment-btn"
              className="h-11 gfs-bg-primary hover:bg-[#044E2A] text-white rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New shipment
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#D2E4D6] overflow-x-auto">
          {[
            { v: "overview", l: "Overview" },
            { v: "collections", l: `Collections (${pending.length + received.length + rejected.length})` },
            { v: "processing", l: `Processing (${processingRuns.length + processingDrafts.length})` },
            { v: "shipments", l: `Shipments (${shipments.length + shipmentDrafts.length})` },
            { v: "destinations", l: "Treatment partners" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`tab-${t.v}`}
              className={`px-4 py-2.5 text-sm font-medium relative whitespace-nowrap ${
                tab === t.v ? "text-[#056839]" : "text-[#3F3F3F]/60 hover:text-[#3F3F3F]"
              }`}
            >
              {t.l}
              {tab === t.v && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#056839]" />
              )}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <FacilityOverview
            data={dashboard}
            setTab={setTab}
            goToShipments={(sub) => { setShipmentSubTab(sub); setTab("shipments"); }}
            resubmissionCount={rejectedShipments.length}
          />
        )}

        {tab === "destinations" && <DestinationsTab />}

        {tab === "shipments" && (
          <div className="space-y-4">
            {/* Sub-tabs (pill style) */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="shipments-subtabs">
              {[
                { v: "drafts",   l: `Drafts (${shipmentDrafts.length})`, accent: "bg-[#FBF6EB] text-[#A0522D] border border-[#E4D5B7]" },
                { v: "pending",  l: `Submitted & awaiting review (${pendingShipments.length})`, accent: "bg-[#E4D5B7] text-[#A0522D]" },
                { v: "approved", l: `Approved (${approvedShipments.length})`, accent: "bg-[#D2E4D6] text-[#056839]" },
                { v: "rejected", l: `Rejected (${rejectedShipments.length})`, accent: "bg-[#A0522D]/15 text-[#A0522D]" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setShipmentSubTab(s.v)}
                  data-testid={`shipments-subtab-${s.v}`}
                  className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
                    shipmentSubTab === s.v
                      ? s.accent + " shadow-sm"
                      : "text-[#3F3F3F]/60 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>
            {shipmentSubTab === "drafts" ? (
              <DraftsTable
                kind="shipment"
                drafts={shipmentDrafts}
                onResume={(d) => navigate(`/facility/shipment?resume=${d.id}`)}
                onDelete={async (d) => {
                  if (!window.confirm("Discard this shipment draft?")) return;
                  try {
                    await api.delete(`/shipments/drafts/${d.id}`);
                    setShipmentDrafts((prev) => prev.filter((x) => x.id !== d.id));
                    toast.success("Draft discarded");
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || e.message);
                  }
                }}
                onNew={() => navigate("/facility/shipment")}
              />
            ) : (
              <ShipmentStatusTable
                shipments={shipmentSubTab === "pending" ? pendingShipments : shipmentSubTab === "approved" ? approvedShipments : rejectedShipments}
                tab={`ship-${shipmentSubTab}`}
                onRowClick={(s, d) => setDetailMain({ kind: "shipped", data: { shipment: s, destination: d }, type: "shipment", id: s.id, admin_status: s.admin_status })}
                onResubmit={(s) => navigate(`/facility/shipment/${s.id}/edit`)}
              />
            )}
          </div>
        )}

        {tab === "processing" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap" data-testid="processing-subtabs">
              {[
                { v: "drafts",    l: `Drafts (${processingDrafts.length})`,      accent: "bg-[#FBF6EB] text-[#A0522D] border border-[#E4D5B7]" },
                { v: "submitted", l: `Submitted (${processingRuns.length})`,     accent: "bg-[#D2E4D6] text-[#056839]" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setProcessingSubTab(s.v)}
                  data-testid={`processing-subtab-${s.v}`}
                  className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
                    processingSubTab === s.v
                      ? s.accent + " shadow-sm"
                      : "text-[#3F3F3F]/60 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>
            {processingSubTab === "drafts" ? (
              <DraftsTable
                kind="processing"
                drafts={processingDrafts}
                onResume={(d) => navigate(`/facility/processing?resume=${d.id}`)}
                onDelete={async (d) => {
                  if (!window.confirm("Discard this processing draft?")) return;
                  try {
                    await api.delete(`/processing/drafts/${d.id}`);
                    setProcessingDrafts((prev) => prev.filter((x) => x.id !== d.id));
                    toast.success("Draft discarded");
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || e.message);
                  }
                }}
                onNew={() => navigate("/facility/processing")}
              />
            ) : (
              <ProcessingRunsTable runs={processingRuns} />
            )}
          </div>
        )}

        {tab === "collections" && (
        <div className="space-y-4">
          {/* Sub-tabs (pill style) */}
          <div className="flex items-center gap-2" data-testid="collections-subtabs">
            {[
              { v: "pending", l: `Pending (${pending.length})`, accent: "bg-[#E4D5B7] text-[#A0522D]" },
              { v: "received", l: `Received (${received.length})`, accent: "bg-[#D2E4D6] text-[#056839]" },
              { v: "rejected", l: `Rejected (${rejected.length})`, accent: "bg-[#A0522D]/15 text-[#A0522D]" },
            ].map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setCollectionSubTab(s.v)}
                data-testid={`collections-subtab-${s.v}`}
                className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
                  collectionSubTab === s.v
                    ? s.accent + " shadow-sm"
                    : "text-[#3F3F3F]/60 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>
          <Card className="border-[#D2E4D6]/80 rounded-xl">
            <CardContent className="p-0">
              <table className="w-full" data-testid="collections-table">
                <thead className="bg-[#D2E4D6]/50">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
                    <th className="px-4 py-3">When &amp; collection</th>
                    <th className="px-4 py-3">Source &amp; address</th>
                    <th className="px-4 py-3">Photo</th>
                    <th className="px-4 py-3">Material</th>
                    <th className="px-4 py-3">Est. weight</th>
                    <th className="px-4 py-3">Recv. weight</th>
                    <th className="px-4 py-3">Paid</th>
                    <th className="px-4 py-3">Receipt</th>
                    <th className="px-4 py-3">Collector</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(collectionSubTab === "pending" ? pending : collectionSubTab === "received" ? received : rejected).map((c) => (
                  <tr key={c.id} className="border-t border-[#D2E4D6]/60 hover:bg-[#F9F8F6] align-top">
                    <td className="px-4 py-3 text-sm text-[#3F3F3F]">
                      <div className="font-medium text-[#000000]">
                        {formatDateTime(c.pickup_at || c.timestamp)}
                      </div>
                      <div className="text-[10px] text-[#3F3F3F]/60 mt-0.5">
                        Submitted {formatDate(c.timestamp)}
                      </div>
                      <RecordId id={c.id} />
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F3F3F] max-w-[260px]">
                      <div className="font-medium text-[#000000]">{c.source_location}</div>
                      {c.address && (
                        <div className="text-[11px] text-[#3F3F3F]/70 mt-0.5 line-clamp-2">
                          {c.address}
                        </div>
                      )}
                      {c.reference_number && (
                        <div
                          className="text-[11px] text-[#056839] font-medium mt-0.5"
                          data-testid={`collection-ref-${c.id}`}
                        >
                          Ref: {c.reference_number}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <PhotoThumbs collection={c} testIdPrefix={`photo-thumb-${c.id}`} />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[#000000]">
                      {(() => {
                        const mats = c.materials || [];
                        const labels = mats.length > 0
                          ? Array.from(new Set(mats.map((m) => m.material_type || m.material).filter(Boolean)))
                          : (c.material_type ? [c.material_type] : []);
                        return labels.length > 0 ? labels.join(", ") : "—";
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#3F3F3F]">
                      {(() => {
                        const mats = c.materials || [];
                        let kg = mats.length > 0
                          ? mats.reduce((s, m) => s + (Number(m.weight_kg) || 0), 0)
                          : 0;
                        if (!kg) kg = Number(c.total_weight_kg) || Number(c.estimated_weight_kg) || 0;
                        return kg ? `${kg} kg` : "—";
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#056839] font-semibold">
                      {c.received_weight_kg ? `${c.received_weight_kg} kg` : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F3F3F] whitespace-nowrap">
                      {c.transaction_amount != null ? (
                        <span className="font-mono text-[#000000]">
                          {Number(c.transaction_amount).toFixed(2)} {c.transaction_currency || ""}
                        </span>
                      ) : (
                        <span className="text-[#3F3F3F]/40 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F3F3F] whitespace-nowrap">
                      {c.payment_proof_path ? (
                        (() => {
                          const isPdf = c.payment_proof_path.toLowerCase().endsWith(".pdf");
                          const href = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURI(c.payment_proof_path)}`;
                          const fileName = c.payment_proof_path.split("/").pop();
                          return isPdf ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`payment-proof-link-${c.id}`}
                              className="inline-flex items-center gap-1 text-[11px] text-[#056839] hover:underline"
                              title={`Open ${fileName}`}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              <span className="max-w-[140px] truncate">{fileName}</span>
                            </a>
                          ) : (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`payment-proof-link-${c.id}`}
                              title="Open payment proof"
                              className="inline-block"
                            >
                              <img
                                src={href}
                                alt="payment proof"
                                className="w-9 h-9 object-cover rounded border border-[#E5DFCF] hover:opacity-80"
                              />
                            </a>
                          );
                        })()
                      ) : (
                        <span className="text-[#3F3F3F]/40 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F3F3F]">
                      {c.collector_name || "—"}
                      {c.collector_id && (
                        <span className="ml-1 text-[10px] text-[#9CA3AF] font-mono" data-testid={`collector-id-${c.id}`}>
                          (ID: {c.collector_id.slice(0, 8)})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.status === "Pending" ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              setConfirmTarget(c);
                              setConfirmAction("Received");
                              setConfirmWeight(String(c.estimated_weight_kg || c.total_weight_kg || ""));
                              setReceiptMaterials(buildReceiptMaterials(c));
                            }}
                            data-testid={`receive-btn-${c.id}`}
                            className="gfs-bg-primary hover:bg-[#044E2A] text-white h-8 px-3"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Receive
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setConfirmTarget(c);
                              setConfirmAction("Rejected");
                              setConfirmWeight(String(c.estimated_weight_kg || c.total_weight_kg || "0"));
                              setReceiptMaterials(buildReceiptMaterials(c));
                            }}
                            data-testid={`reject-btn-${c.id}`}
                            className="border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white h-8 px-3"
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-[#3F3F3F]/60">
                          {c.status === "Received" && c.used_in_processing && "Processed"}
                          {c.status === "Received" && !c.used_in_processing && "Awaiting processing"}
                          {c.status === "Rejected" && (
                            <span className="text-[#A0522D]" title={c.rejection_reason}>
                              {c.rejection_reason
                                ? c.rejection_reason.length > 40
                                  ? c.rejection_reason.slice(0, 40) + "…"
                                  : c.rejection_reason
                                : "—"}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(collectionSubTab === "pending" ? pending : collectionSubTab === "received" ? received : rejected).length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-[#3F3F3F]/60">
                      <Factory className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      No {collectionSubTab === "pending" ? "pending" : collectionSubTab === "received" ? "received" : "rejected"} collections yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
        </div>
        )}
      </div>

      {/* Detail dialog & resubmit dialog for shipment status tabs */}
      <HistoryDetailDialog detail={detailMain} onClose={() => setDetailMain(null)} />

      {/* Confirm dialog */}
      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent data-testid="receipt-dialog" className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {confirmAction === "Received" ? "Confirm receipt" : "Reject delivery"}
            </DialogTitle>
            {confirmTarget?.id && (
              <div className="mt-1"><RecordId id={confirmTarget.id} /></div>
            )}
          </DialogHeader>
          {confirmTarget && (
                <>
                {(confirmTarget.photos?.length > 0 || confirmTarget?.photo_path) && (
                  <div className="space-y-2" data-testid="receipt-photos-grid">
                    {(confirmTarget.photos?.length > 0
                      ? confirmTarget.photos
                      : [{ path: confirmTarget?.photo_path }]
                    ).map((p, i) => (
                      <PhotoView key={(p && (p.path || p.storage_path)) || `photo-${i}`} path={p.path || p.storage_path || p} />
                    ))}
                  </div>
                )}
                </>
              )}
              {/* Collector payment proof - show for review */}
            {confirmTarget?.payment_proof_path && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-xs uppercase tracking-wider text-amber-700 font-medium mb-2">Submitted payment proof</div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(confirmTarget.payment_proof_path)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-[#056839] text-white text-xs font-medium hover:bg-[#044E2A]"
                  >
                    View proof
                  </a>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(confirmTarget.payment_proof_path)}`}
                    download
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-[#056839] text-[#056839] text-xs font-medium hover:bg-[#F0FAF4]"
                  >
                    ↓ Download
                  </a>
                </div>
              </div>
            )}
            <div className="text-sm bg-[#D2E4D6]/30 rounded-lg p-3 space-y-1">
                <div>
                  <span className="text-[#69A488] uppercase text-xs tracking-wider">Site</span>{" "}
                  <span className="font-medium">{confirmTarget?.source_location}</span>
                </div>
                {confirmTarget?.collector_name && (
                  <div>
                    <span className="text-[#69A488] uppercase text-xs tracking-wider">Collector</span>{" "}
                    <span className="font-medium" data-testid="receipt-collector-name">
                      {confirmTarget.collector_name}{idSuffix(confirmTarget.collector_id)}
                      {confirmTarget?.collector_email ? (
                        <span className="text-[#3F3F3F]/60 ml-1">({confirmTarget.collector_email})</span>
                      ) : null}
                    </span>
                  </div>
                )}
                {confirmTarget?.address && (
                  <div>
                    <span className="text-[#69A488] uppercase text-xs tracking-wider">Address</span>{" "}
                    <span className="font-medium">{confirmTarget?.address}</span>
                  </div>
                )}
                {confirmTarget?.reference_number && (
                  <div>
                    <span className="text-[#69A488] uppercase text-xs tracking-wider">Reference</span>{" "}
                    <span className="font-medium" data-testid="receipt-reference-number">
                      {confirmTarget.reference_number}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-[#69A488] uppercase text-xs tracking-wider">Collection</span>{" "}
                  <span className="font-medium">
                    {formatDateTime(confirmTarget?.pickup_at || confirmTarget?.timestamp)}
                  </span>
                </div>
                <div>
                  <span className="text-[#69A488] uppercase text-xs tracking-wider">Collector-declared total</span>{" "}
                  <span className="font-mono">
                    {(() => {
                      let kg = receiptMaterials.reduce((s, m) => s + (Number(m.estimated_weight_kg) || 0), 0);
                      if (!kg) kg = Number(confirmTarget?.total_weight_kg) || Number(confirmTarget?.estimated_weight_kg) || 0;
                      return kg ? `${kg} kg` : "—";
                    })()}
                  </span>
                </div>
              </div>

              {/* Per-material breakdown — each row is editable so the facility
                  can adjust the material type and/or the received weight on
                  receipt. For rejection, only the type+weight context is shown. */}
              <div className="rounded-lg border border-[#D2E4D6] p-3 space-y-2" data-testid="receipt-materials-block">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">
                    {confirmAction === "Received"
                      ? "Materials & actual received weight"
                      : "Materials & weight at delivery"}
                  </label>
                  {confirmAction === "Received" && (
                    <button
                      type="button"
                      onClick={() =>
                        setReceiptMaterials((rows) => [
                          ...rows,
                          { material_type: "PET", estimated_weight_kg: 0, received_weight_kg: "" },
                        ])
                      }
                      data-testid="receipt-materials-add-row"
                      className="text-xs text-[#056839] underline"
                    >
                      + add row
                    </button>
                  )}
                </div>

                {receiptMaterials.length === 0 ? (
                  <div className="text-xs text-[#3F3F3F]/60">No materials reported.</div>
                ) : (
                  <div className="space-y-2">
                    {receiptMaterials.map((row, idx) => (
                      <div
                        key={`${row.material_type}-${idx}`}
                        className="grid grid-cols-12 gap-2 items-end"
                        data-testid={`receipt-materials-row-${idx}`}
                      >
                        <div className="col-span-5">
                          <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/60 mb-1">Material</div>
                          <select
                            value={row.material_type}
                            onChange={(e) =>
                              setReceiptMaterials((rows) =>
                                rows.map((r, i) =>
                                  i === idx ? { ...r, material_type: e.target.value } : r
                                )
                              )
                            }
                            data-testid={`receipt-materials-type-${idx}`}
                            className="h-10 w-full rounded-md border border-[#D2E4D6] bg-white px-2 text-sm"
                          >
                            {MATERIAL_OPTIONS.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                            {row.material_type && !MATERIAL_OPTIONS.includes(row.material_type) && (
                              <option value={row.material_type}>{row.material_type}</option>
                            )}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/60 mb-1">Collector-declared</div>
                          <div className="h-10 rounded-md bg-[#F9F8F6] border border-[#D2E4D6] px-2 flex items-center text-sm font-mono text-[#3F3F3F]">
                            {row.estimated_weight_kg || 0} kg
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/60 mb-1">
                            {confirmAction === "Received" ? "Received (kg)" : "Delivered (kg)"}
                          </div>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={row.received_weight_kg}
                            onChange={(e) =>
                              setReceiptMaterials((rows) =>
                                rows.map((r, i) =>
                                  i === idx ? { ...r, received_weight_kg: e.target.value } : r
                                )
                              )
                            }
                            data-testid={`receipt-materials-received-${idx}`}
                            className="h-10"
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {confirmAction === "Received" && receiptMaterials.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setReceiptMaterials((rows) => rows.filter((_, i) => i !== idx))
                              }
                              data-testid={`receipt-materials-remove-${idx}`}
                              className="h-10 w-10 inline-flex items-center justify-center text-[#A0522D] hover:bg-[#A0522D]/10 rounded-md"
                              title="Remove row"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="pt-1 text-right text-xs text-[#3F3F3F]/70" data-testid="receipt-materials-total">
                      Total received:{" "}
                      <span className="font-mono font-semibold text-[#056839]">
                        {receiptMaterials.reduce((s, r) => s + (Number(r.received_weight_kg) || 0), 0)} kg
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {confirmAction === "Rejected" && (
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#A0522D] font-medium">
                    Rejection reason <span className="normal-case tracking-normal">*required</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g., contaminated, wrong material, water-damaged…"
                    rows={3}
                    data-testid="rejection-reason-input"
                    className="mt-2 w-full rounded-lg border-2 border-[#A0522D]/40 focus:border-[#A0522D] focus:outline-none focus:ring-2 focus:ring-[#A0522D]/15 px-3 py-2 text-sm bg-white"
                  />
                </div>
              )}
              {confirmAction === "Received" && (
                <div className="bg-[#F9F8F6] border border-[#E4D5B7]/60 rounded-xl p-4 space-y-3" data-testid="receipt-transaction-block">
                  <div className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">
                    Transaction amount <span className="text-[#A0522D] normal-case tracking-normal">*required</span>
                    {confirmTarget?.transaction_amount != null && (
                      <span className="normal-case tracking-normal text-[#3F3F3F]/60 ml-1">
                        (collector entered an amount — adjust if needed)
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <div
                      data-testid="receipt-tx-currency-static"
                      className="h-11 rounded-lg border-2 border-[#E4D5B7] bg-[#F9F8F6] px-3 text-sm text-[#3F3F3F] flex items-center font-medium"
                    >
                      RM
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={txAmount}
                      onChange={(e) => setTxAmount(e.target.value)}
                      placeholder="Amount paid to the source"
                      data-testid="receipt-tx-amount-input"
                      required
                      className="h-11"
                    />
                  </div>
                  <input
                    ref={proofRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid="receipt-proof-input"
                    onChange={(e) => uploadReceiptProof(e.target.files?.[0])}
                  />
                  {!proofPath ? (
                    <button
                      type="button"
                      onClick={() => proofRef.current?.click()}
                      disabled={proofUploading}
                      data-testid="receipt-proof-pick"
                      className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 h-11 rounded-lg border-2 border-dashed border-[#69A488]/60 bg-white hover:border-[#69A488] text-[#3F3F3F] disabled:opacity-60"
                    >
                      {proofUploading ? "Uploading…" : "Attach bank transfer proof (optional)"}
                    </button>
                  ) : (
                    <div className="w-full inline-flex items-center gap-2 text-sm px-3 h-11 rounded-lg bg-[#D2E4D6] text-[#056839] font-medium" data-testid="receipt-proof-attached">
                      <Check className="w-4 h-4 shrink-0" />
                      <span className="flex-1 truncate">{proofFilename || "Attached"}</span>
                      <button
                        type="button"
                        onClick={() => { setProofPath(""); setProofFilename(""); if (proofRef.current) proofRef.current.value = ""; }}
                        className="text-[#A0522D]"
                        aria-label="Remove proof"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <p className="text-[11px] text-[#3F3F3F]/60">
                    Transaction amount is required. A bank transfer proof is optional —
                    attach one only if the collector didn&apos;t already submit one.
                  </p>
                </div>
              )}
          <div className="space-y-2 mt-3" data-testid="receipt-photos-block">
            <label className="block text-sm font-medium text-gray-700">
              Facility photos <span className="text-gray-400 font-normal">(up to 3, optional)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {receiptPhotos.map(p => (
                <div key={p.idx} className="relative">
                  <img src={p.preview} className="w-16 h-16 object-cover rounded-lg border" alt="" />
                  <div className="text-xs text-gray-400 text-center mt-1 w-16 truncate">
                    {formatTime(p.timestamp)}
                  </div>
                  <button type="button" onClick={() => setReceiptPhotos(prev => prev.filter(x => x.idx !== p.idx))}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">
                    ×
                  </button>
                </div>
              ))}
              {receiptPhotos.length < 3 && (
                <label className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-green-400" data-testid="receipt-photo-add">
                  <span className="text-xl text-gray-400">+</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleReceiptPhotoAdd} />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmTarget(null)}
              data-testid="receipt-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReceipt}
              disabled={busy}
              data-testid="receipt-confirm-btn"
              className={
                confirmAction === "Received"
                  ? "gfs-bg-primary hover:bg-[#044E2A] text-white"
                  : "bg-[#A0522D] hover:bg-[#804124] text-white"
              }
            >
              {confirmAction === "Received" ? "Confirm receipt" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DraftsTable({ kind, drafts, onResume, onDelete, onNew }) {
  if (!drafts || drafts.length === 0) {
    return (
      <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={`empty-${kind}-drafts`}>
        <CardContent className="p-12 text-center">
          <Save className="w-10 h-10 text-[#69A488] mx-auto mb-3 opacity-60" />
          <p className="text-sm font-medium text-[#3F3F3F]">No saved drafts</p>
          <p className="text-xs text-[#3F3F3F]/60 mt-1 mb-4">
            Start a new {kind === "shipment" ? "shipment" : "processing run"} and hit &quot;Save draft&quot; to preserve
            partial entries here.
          </p>
          <Button
            onClick={onNew}
            data-testid={`empty-${kind}-drafts-new-btn`}
            className="gfs-bg-primary hover:bg-[#044E2A] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New {kind === "shipment" ? "shipment" : "processing"}
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full" data-testid={`${kind}-drafts-table`}>
          <thead className="bg-[#D2E4D6]/50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
              <th className="px-4 py-3">Draft</th>
              <th className="px-4 py-3">Contents</th>
              <th className="px-4 py-3">Last saved</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((d) => {
              const isShip = kind === "shipment";
              const secondary = isShip
                ? `${(d.destinations || []).length} partner${(d.destinations || []).length !== 1 ? "s" : ""} · ${(d.source_output_ids || []).length} output batch${(d.source_output_ids || []).length !== 1 ? "es" : ""}`
                : `${(d.outputs || []).length} output batch${(d.outputs || []).length !== 1 ? "es" : ""} · ${(d.input_collection_ids || []).length} input collection${(d.input_collection_ids || []).length !== 1 ? "s" : ""}`;
              return (
                <tr
                  key={d.id}
                  data-testid={`${kind}-draft-row-${d.id}`}
                  className="border-t border-[#D2E4D6]/60 hover:bg-[#F9F8F6] align-top"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-[#000000]">{d.label || "Untitled draft"}</div>
                    <RecordId id={d.id} />
                  </td>
                  <td className="px-4 py-3 text-sm text-[#3F3F3F]/80">{secondary}</td>
                  <td className="px-4 py-3 text-sm text-[#3F3F3F]">{formatDateTime(d.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onResume(d)}
                        data-testid={`${kind}-draft-resume-${d.id}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                      >
                        Resume & edit
                      </button>
                      <button
                        onClick={() => onDelete(d)}
                        data-testid={`${kind}-draft-delete-${d.id}`}
                        className="p-1 rounded-md text-[#A0522D] hover:bg-[#A0522D]/10"
                        title="Discard draft"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ProcessingRunsTable({ runs }) {
  if (!runs || runs.length === 0) {
    return (
      <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="empty-processing-submitted">
        <CardContent className="p-12 text-center">
          <Cog className="w-10 h-10 text-[#69A488] mx-auto mb-3 opacity-60" />
          <p className="text-sm font-medium text-[#3F3F3F]">No processing runs yet</p>
          <p className="text-xs text-[#3F3F3F]/60 mt-1">
            Every run you submit will appear here with its inputs, materials, and residual weight.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full" data-testid="processing-submitted-table">
          <thead className="bg-[#D2E4D6]/50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
              <th className="px-4 py-3">Processed on</th>
              <th className="px-4 py-3">Types</th>
              <th className="px-4 py-3">Outputs (material · form · kg)</th>
              <th className="px-4 py-3 text-right">Input kg</th>
              <th className="px-4 py-3 text-right">Output kg</th>
              <th className="px-4 py-3 text-right">Residual</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((p) => {
              const totalOut = (p.outputs || []).reduce((s, o) => s + (Number(o.weight_kg) || 0), 0);
              return (
                <tr
                  key={p.id}
                  data-testid={`processing-run-row-${p.id}`}
                  className="border-t border-[#D2E4D6]/60 hover:bg-[#F9F8F6] align-top"
                >
                  <td className="px-4 py-3 text-sm text-[#3F3F3F]">
                    <div>{formatDateTime(p.processing_date)}</div>
                    <RecordId id={p.id} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.processing_types || []).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#3F3F3F]">
                    {(p.outputs || []).length === 0
                      ? <span className="text-[#3F3F3F]/40">—</span>
                      : (
                        <div className="flex flex-col gap-0.5">
                          {(p.outputs || []).map((o, i) => (
                            <div key={o.id || i} className="flex items-baseline gap-2">
                              <span className="font-semibold text-[#056839]">{o.material_type}</span>
                              <span className="text-[#3F3F3F]/70">{o.form}</span>
                              <span>{Number(o.weight_kg || 0).toFixed(1)} kg</span>
                              {o.shipped && (
                                <span className="ml-1 text-[9px] uppercase tracking-wider text-[#69A488]">shipped</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-[#3F3F3F]">
                    {Number(p.total_input_weight_kg || 0).toFixed(1)} kg
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-[#056839] font-semibold">
                    {totalOut.toFixed(1)} kg
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-[#A0522D]">
                    {Number(p.residual_weight_kg || 0).toFixed(1)} kg
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ShipmentStatusTable({ shipments, tab, onRowClick, onResubmit }) {
  const empty = {
    "ship-pending":  { title: "No pending shipments",  sub: "All your submitted shipments have been reviewed." },
    "ship-approved": { title: "No approved shipments yet", sub: "Approved shipments will appear here." },
    "ship-rejected": { title: "No rejected shipments",  sub: "Nice — admin hasn't flagged any of your shipments." },
  }[tab];
  if (!shipments || shipments.length === 0) {
    return (
      <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={`empty-${tab}`}>
        <CardContent className="p-12 text-center">
          <Truck className="w-10 h-10 text-[#69A488] mx-auto mb-3 opacity-60" />
          <p className="text-sm font-medium text-[#3F3F3F]">{empty.title}</p>
          <p className="text-xs text-[#3F3F3F]/60 mt-1">{empty.sub}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full" data-testid={`shipments-table-${tab}`}>
          <thead className="bg-[#D2E4D6]/50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3">Destinations</th>
              <th className="px-4 py-3">Materials</th>
              <th className="px-4 py-3 text-right">Total shipped</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((s) => {
              const firstDest = (s.destinations || [])[0] || {};
              const otherCount = Math.max(0, (s.destinations || []).length - 1);
              const materials = Array.from(new Set((s.destinations || []).map(d => d.material_type).filter(Boolean)));
              return (
                <tr
                  key={s.id}
                  onClick={() => onRowClick(s, firstDest)}
                  data-testid={`shipment-row-${s.id}`}
                  className="border-t border-[#D2E4D6]/60 hover:bg-[#F9F8F6] cursor-pointer align-top"
                >
                  <td className="px-4 py-3 text-sm text-[#3F3F3F]">
                    <div>{formatDateTime(s.created_at)}</div>
                    <RecordId id={s.id} />
                  </td>
                  <td className="px-4 py-3 text-sm text-[#000000] max-w-[260px]">
                    <div className="font-medium truncate flex items-center gap-1.5">
                      <span className="truncate">{firstDest.destination_name || s.destination_name || "—"}</span>
                      {s.shipment_source === "surplus_sale" && (
                        <span
                          className="shrink-0 px-1.5 py-0.5 rounded-md bg-[#A0522D]/15 text-[#A0522D] text-[9px] font-semibold uppercase tracking-wider"
                          title="Surplus sale — no linked processing batch"
                          data-testid={`facility-shipment-surplus-${s.id}`}
                        >
                          Surplus
                        </span>
                      )}
                    </div>
                    {firstDest.destination_type && (
                      <div className="text-[10px] uppercase tracking-wider text-[#69A488] mt-0.5">{firstDest.destination_type}</div>
                    )}
                    {otherCount > 0 && <div className="text-[11px] text-[#3F3F3F]/60 mt-0.5">+{otherCount} more</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {materials.length === 0 && <span className="text-xs text-[#3F3F3F]/40">—</span>}
                      {materials.map(m => (
                        <span key={m} className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">{m}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-[#056839] font-semibold">
                    {Number(s.total_shipped_kg || 0).toFixed(1)} kg
                  </td>
                  <td className="px-4 py-3">
                    {tab === "ship-pending" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#E4D5B7] text-[#A0522D]">
                        Awaiting admin review
                      </span>
                    )}
                    {tab === "ship-approved" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#D2E4D6] text-[#056839]">
                        Approved
                      </span>
                    )}
                    {tab === "ship-rejected" && (
                      <div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#A0522D]/15 text-[#A0522D]">
                          Rejected
                        </span>
                        {s.rejection_reason && (
                          <div className="text-[11px] text-[#A0522D] mt-1 line-clamp-2 max-w-[260px]" title={s.rejection_reason}>
                            {s.rejection_reason}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {tab === "ship-rejected" ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onResubmit(s); }}
                        data-testid={`resubmit-btn-${s.id}`}
                        className="px-3 h-8 text-xs border border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white rounded-md transition-colors"
                      >
                        Re-edit & resubmit
                      </button>
                    ) : (
                      <span className="text-xs text-[#3F3F3F]/40">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}


function StatCard({ label, value, icon, tint, text }) {
  return (
    <div
      className="rounded-xl p-5 md:p-6 border border-[#D2E4D6]/60 bg-white shadow-sm transition-shadow hover:shadow-md flex items-start justify-between"
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">{label}</div>
        <div
          className="font-heading text-4xl font-semibold tracking-tighter mt-2"
          style={{ color: text }}
        >
          {value}
        </div>
      </div>
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: tint, color: text }}
      >
        {icon}
      </div>
    </div>
  );
}

function PhotoView({ path }) {
  if (!path) return null;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(path)}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img
        src={src}
        alt="Collection"
        data-testid="receipt-photo"
        className="w-full max-h-[380px] object-contain rounded-lg border border-[#D2E4D6]/80 bg-white"
        onError={(e) => {
          const a = e.currentTarget.closest("a");
          if (a) a.style.display = "none";
          else e.currentTarget.style.display = "none";
        }}
      />
    </a>
  );
}


function PhotoThumb({ path, testId }) {
  if (!path) return <span className="text-xs text-[#3F3F3F]/40">—</span>;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(path)}`;
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="block w-14 h-14 rounded-md overflow-hidden border border-[#D2E4D6] hover:scale-110 transition-transform"
    >
      <img
        src={src}
        alt="collection"
        className="w-full h-full object-cover"
        onError={(e) => {
          const a = e.currentTarget.closest("a");
          if (a) a.style.display = "none";
          else e.currentTarget.style.display = "none";
        }}
      />
    </a>
  );
}


function PhotoThumbs({ collection, testIdPrefix }) {
  // Render up to 3 thumbnails inline; if there are more, show a "+N" badge.
  const photos = Array.isArray(collection?.photos) ? collection.photos : [];
  const paths = photos
    .map((p) => (p && typeof p === "object" ? (p.path || p.storage_path) : p))
    .filter(Boolean);
  if (paths.length === 0 && collection?.photo_path) paths.push(collection.photo_path);
  if (paths.length === 0) return <span className="text-xs text-[#3F3F3F]/40">—</span>;

  const visible = paths.slice(0, 3);
  const extra = paths.length - visible.length;

  return (
    <div className="flex items-center gap-1" data-testid={testIdPrefix}>
      {visible.map((p, i) => (
        <PhotoThumb key={typeof p === "string" ? p : (p?.path || p?.storage_path || `photo-${i}`)} path={p} testId={`${testIdPrefix}-${i}`} />
      ))}
      {extra > 0 && (
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-[#D2E4D6] text-[#056839] text-xs font-semibold"
          data-testid={`${testIdPrefix}-more`}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

function FacilityOverview({ data, setTab, goToShipments, resubmissionCount = 0 }) {
  if (!data) {
    return (
      <div className="text-sm text-[#3F3F3F]/60 italic" data-testid="overview-loading">
        Loading metrics…
      </div>
    );
  }
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="facility-overview">
      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
        <KPI
          label="Raw in stock"
          value={`${t.raw_in_stock_kg.toFixed(0)} kg`}
          sub="Received, awaiting processing"
          icon={<Warehouse className="w-5 h-5" />}
          tint="#E4D5B7"
          color="#A0522D"
          testId="kpi-raw-stock"
        />
        <KPI
          label="Processed in stock"
          value={`${(t.processed_in_stock_kg || 0).toFixed(1)} kg`}
          sub="Output batches awaiting shipment"
          icon={<Package className="w-5 h-5" />}
          tint="#D2E4D6"
          color="#056839"
          testId="kpi-processed-stock"
        />
        <KPI
          label="Shipped"
          value={`${t.shipped_kg.toFixed(0)} kg`}
          sub={`${t.shipments_count} shipments`}
          icon={<Truck className="w-5 h-5" />}
          tint="#056839"
          color="#FFFFFF"
          testId="kpi-shipped"
        />
        <button
          type="button"
          onClick={() => goToShipments && goToShipments("rejected")}
          data-testid="kpi-resubmission-required"
          className={`text-left rounded-xl p-5 md:p-6 border shadow-sm transition-all hover:shadow-md ${resubmissionCount > 0 ? "border-[#A0522D] bg-[#A0522D]/5 hover:border-[#A0522D] cursor-pointer" : "border-[#D2E4D6]/60 bg-white"}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#A0522D] font-medium">
                Resubmission required
              </div>
              <div className="font-heading text-3xl md:text-4xl font-semibold tracking-tighter mt-2 text-[#A0522D]">
                {resubmissionCount}
              </div>
              <div className="text-xs mt-1 text-[#3F3F3F]/70">
                {resubmissionCount > 0 ? "Rejected shipments — click to review" : "No action needed"}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#A0522D]/15 text-[#A0522D]">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
        </button>
      </div>

      {/* Pending collection requests — only one stat now */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        <SmallStat
          label="Pending collection requests"
          value={t.pending_count}
          accent="#A0522D"
          onClick={() => setTab && setTab("collections")}
          testId="smallstat-pending"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock by material */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Stock</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000] mb-3">
              Materials in stock
            </h3>
            <div className="h-56" data-testid="chart-processed-stock">
              {data.processed_in_stock_by_material.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.processed_in_stock_by_material}>
                    <XAxis
                      dataKey="material_type"
                      stroke="#3F3F3F"
                      tick={{ fill: "#3F3F3F", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#3F3F3F"
                      tick={{ fill: "#3F3F3F", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RTooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #D2E4D6",
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="kg" fill="#056839" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            {data.raw_in_stock_by_material.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#D2E4D6] text-xs">
                <div className="uppercase tracking-wider text-[#69A488] mb-1">Raw in stock</div>
                <div className="flex flex-wrap gap-2">
                  {data.raw_in_stock_by_material.map((m) => (
                    <span
                      key={m.material_type}
                      className="px-2 py-1 rounded-md bg-[#E4D5B7]/50 text-[#3F3F3F] font-medium"
                    >
                      {m.material_type}{" "}
                      <span className="font-mono text-[#A0522D]">{m.kg.toFixed(1)} kg</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shipped by destination type */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Outbound</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000] mb-3">
              Shipped by destination type
            </h3>
            <div className="h-56" data-testid="chart-by-outcome">
              {data.by_outcome.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_outcome} layout="vertical">
                    <XAxis
                      type="number"
                      stroke="#3F3F3F"
                      tick={{ fill: "#3F3F3F", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      dataKey="destination_type"
                      type="category"
                      stroke="#3F3F3F"
                      tick={{ fill: "#3F3F3F", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={90}
                    />
                    <RTooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #D2E4D6",
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="kg" radius={[0, 6, 6, 0]}>
                      {data.by_outcome.map((e, i) => {
                        const colorMap = {
                          Recycler: "#056839",
                          Converter: "#69A488",
                          Incinerator: "#A0522D",
                          Landfill: "#3F3F3F",
                        };
                        return <Cell key={e.destination_type || `cell-${i}`} fill={colorMap[e.destination_type] || "#69A488"} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent activity history */}
      <RecentActivityCard />
    </div>
  );
}

function KPI({ label, value, sub, icon, tint, color, testId }) {
  const useDark = tint === "#056839";
  return (
    <div
      className={`rounded-xl p-5 md:p-6 border shadow-sm transition-shadow hover:shadow-md ${
        useDark ? "" : "bg-white border-[#D2E4D6]/60"
      }`}
      style={useDark ? { background: "#056839", borderColor: "#056839" } : {}}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.15em] font-medium ${
              useDark ? "text-[#D2E4D6]" : "text-[#69A488]"
            }`}
          >
            {label}
          </div>
          <div
            className="font-heading text-3xl md:text-4xl font-semibold tracking-tighter mt-2"
            style={{ color: useDark ? "#FFFFFF" : color || "#056839" }}
          >
            {value}
          </div>
          <div className={`text-xs mt-1 ${useDark ? "text-white/70" : "text-[#3F3F3F]/70"}`}>
            {sub}
          </div>
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{
            background: useDark ? "rgba(255,255,255,0.15)" : tint || "#D2E4D6",
            color: useDark ? "#FFFFFF" : color || "#056839",
          }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SmallStat({ label, value, accent, onClick, testId }) {
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left bg-white border border-[#D2E4D6]/60 rounded-xl p-4 flex items-baseline justify-between ${
        clickable ? "hover:shadow-md hover:border-[#056839] transition-all cursor-pointer" : ""
      }`}
    >
      <span className="text-xs uppercase tracking-wider text-[#69A488]">{label}</span>
      <span className="font-heading text-2xl font-semibold" style={{ color: accent }}>
        {value}
      </span>
    </Tag>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center text-sm text-[#3F3F3F]/50">
      No data yet
    </div>
  );
}

function RecentActivityCard() {
  const [tab, setTab] = useState("received");
  const [detail, setDetail] = useState(null); // { kind, data }
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    material_type: "",
    collector_id: "",
    destination_type: "",
    destination_name: "",
  });
  const [shipmentStatusFilter, setShipmentStatusFilter] = useState("");
  const [lookup, setLookup] = useState({ materials: [], destination_types: [], collectors: [], destinations: [] });
  const [collections, setCollections] = useState([]);
  const [processings, setProcessings] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportBytes, setExportBytes] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/lookup/filters").then((r) => setLookup(r.data)).catch(() => {});
  }, []);

  function buildParams(forKind) {
    const p = {};
    if (filters.from) p.from = new Date(filters.from).toISOString();
    if (filters.to) p.to = new Date(filters.to).toISOString();
    if (filters.material_type) p.material_type = filters.material_type;
    if (forKind === "received" && filters.collector_id) p.collector_id = filters.collector_id;
    if (forKind === "shipped") {
      if (filters.destination_type) p.destination_type = filters.destination_type;
      if (filters.destination_name) p.destination_name = filters.destination_name;
    }
    return p;
  }

  async function loadTab() {
    setLoading(true);
    try {
      if (tab === "received") {
        const { data } = await api.get("/collections", {
          params: { status: "Received", ...buildParams("received") },
        });
        setCollections(data);
      } else if (tab === "processed") {
        const { data } = await api.get("/processing", { params: buildParams("processed") });
        setProcessings(data);
      } else if (tab === "shipped") {
        const { data } = await api.get("/shipments", { params: buildParams("shipped") });
        setShipments(data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadTab();
  }, [tab, filters]);

  const tabs = [
    { v: "received", l: `Received (${collections.length})`, testId: "history-tab-received" },
    { v: "processed", l: `Processed (${processings.length})`, testId: "history-tab-processed" },
    { v: "shipped", l: `Shipped (${shipments.length})`, testId: "history-tab-shipped" },
  ];
  const fmtTs = (s) => {
    if (!s) return "—";
    try { return formatDateTime(s); } catch { return s; }
  };

  const showCollectorFilter = tab === "received";
  const showDestinationFilters = tab === "shipped";

  // Filtered shipments (client-side admin_status filter on top of server filters)
  const visibleShipments = shipmentStatusFilter
    ? shipments.filter((s) => (s.admin_status || "pending") === shipmentStatusFilter)
    : shipments;

  function downloadCsv(rows, filename) {
    if (!rows || rows.length === 0) {
      toast.error("Nothing to export with current filters");
      return;
    }
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Prepend a date-range + generated-at metadata block so the exported CSV
    // matches what the dashboard shows. Mirrors the compliance ZIP README.
    const rangeLabel = (filters.from || filters.to)
      ? `${filters.from ? new Date(filters.from).toISOString() : "All time"} to ${filters.to ? new Date(filters.to).toISOString() : "now"}`
      : "All time";
    const meta = [
      `# PROcess facility export`,
      `# Date range: ${rangeLabel}`,
      `# Generated: ${new Date().toISOString()}`,
      ``,
    ].join("\n");
    const csv = meta + [headers.join(",")].concat(rows.map((r) => headers.map((h) => esc(r[h])).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  async function exportCompliancePackage() {
    // Stream the evidence ZIP via blob so the browser downloads it directly.
    // Uses the same date range as the CSV export so labels match. Progress
    // callback updates the button copy with bytes streamed so the admin sees
    // the download is actually making forward progress on a slow pull.
    if (exportBusy) return;
    setExportBusy(true);
    setExportBytes(0);
    const toastId = toast.loading("Preparing compliance package…");
    try {
      const params = {};
      if (filters.from) params.from = new Date(filters.from).toISOString();
      if (filters.to) params.to = new Date(filters.to).toISOString();
      const res = await api.get("/facility/export.zip", {
        params,
        responseType: "blob",
        onDownloadProgress: (evt) => {
          const loaded = evt.loaded || 0;
          setExportBytes(loaded);
          toast.loading(
            `Downloading evidence package… ${fmtMB(loaded)} received`,
            { id: toastId },
          );
        },
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const label_from = filters.from || "all";
      const label_to = filters.to || "now";
      a.download = `compliance_package_${label_from}_${label_to}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Compliance package downloaded · ${fmtMB(blob.size)}`, { id: toastId });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not export package", { id: toastId });
    } finally {
      setExportBusy(false);
      setExportBytes(0);
    }
  }

  function exportFiltered() {
    // Per-material columns, GMT+X timestamps — same schema as the server-side
    // /api/admin/export.csv so facility/admin/viewer exports are all
    // consistent. We build client-side here because facility role isn't
    // allowed to hit the admin CSV endpoint.
    const MATS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];
    const offsetH = 8;
    const fmtDt = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      const local = new Date(d.getTime() + offsetH * 3600_000);
      const pad = (n) => String(n).padStart(2, "0");
      const s = `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
      const sign = offsetH >= 0 ? "+" : "-";
      return `${s} (GMT${sign}${pad(Math.abs(offsetH))}:00)`;
    };
    const matBreakdown = (mats, fbType, fbKg) => {
      const out = Object.fromEntries(MATS.map((m) => [m, 0]));
      let hit = false;
      for (const m of mats || []) {
        const mt = m?.material_type;
        if (mt && mt in out) {
          out[mt] += Number(m?.weight_kg || 0);
          hit = true;
        }
      }
      if (!hit && fbType && fbType in out) out[fbType] = Number(fbKg || 0);
      return out;
    };
    const matCols = (bd) => Object.fromEntries(MATS.map((m) => [`mat_${m}_kg`, Number((bd[m] || 0).toFixed(3))]));

    if (tab === "received") {
      const rows = collections.map((c) => {
        const bd = matBreakdown(
          c.materials || [],
          c.material_type,
          c.received_weight_kg ?? c.estimated_weight_kg ?? c.total_weight_kg ?? 0,
        );
        return {
          id: c.id || "",
          date_received: fmtDt(c.date_received || c.timestamp),
          source: c.source_location || "",
          collector: c.collector_name || "",
          estimated_total_kg: c.estimated_weight_kg ?? "",
          received_total_kg: c.received_weight_kg ?? "",
          ...matCols(bd),
          transaction_amount: c.transaction_amount ?? "",
          currency: c.transaction_currency || "",
          status: c.status || "",
          photo_path: c.photo_path || c.photo_filename || "",
          payment_proof_path: c.payment_proof_path || "",
        };
      });
      downloadCsv(rows, `collections_received_${Date.now()}.csv`);
    } else if (tab === "processed") {
      const rows = processings.map((p) => {
        const outBd = Object.fromEntries(MATS.map((m) => [m, 0]));
        for (const o of p.outputs || []) {
          if (o?.material_type && o.material_type in outBd) outBd[o.material_type] += Number(o?.weight_kg || 0);
        }
        const inBd = Object.fromEntries(MATS.map((m) => [m, 0]));
        for (const inp of p.input_collections_snapshot || p.inputs || []) {
          const bd = matBreakdown(
            inp.materials || [],
            inp.material_type,
            inp.received_weight_kg ?? inp.total_weight_kg ?? 0,
          );
          for (const m of MATS) inBd[m] += bd[m];
        }
        const inputCols = Object.fromEntries(MATS.map((m) => [`input_${m}_kg`, Number(inBd[m].toFixed(3))]));
        const outputCols = Object.fromEntries(MATS.map((m) => [`output_${m}_kg`, Number(outBd[m].toFixed(3))]));
        return {
          id: p.id || "",
          processing_date: fmtDt(p.processing_date),
          types: (p.processing_types || []).join(", "),
          total_input_kg: p.total_input_weight_kg ?? "",
          total_output_kg: Number(Object.values(outBd).reduce((a, b) => a + b, 0).toFixed(3)),
          residual_kg: Number((p.residual_weight_kg || 0).toFixed(3)),
          ...inputCols,
          ...outputCols,
        };
      });
      downloadCsv(rows, `processing_${Date.now()}.csv`);
    } else if (tab === "shipped") {
      // One row per SHIPMENT (was previously one-per-destination) with
      // per-material `shipped_<MT>_kg` columns so mixed shipments aren't
      // pipe-joined into one cell.
      const rows = visibleShipments.map((s) => {
        const dests = s.destinations || [];
        const bd = Object.fromEntries(MATS.map((m) => [m, 0]));
        for (const d of dests) {
          if (d?.material_type && d.material_type in bd) bd[d.material_type] += Number(d?.weight_kg || 0);
        }
        const shippedCols = Object.fromEntries(MATS.map((m) => [`shipped_${m}_kg`, Number(bd[m].toFixed(3))]));
        const dates = dests.map((d) => (d.transaction_at ? new Date(d.transaction_at).getTime() : NaN)).filter((n) => !Number.isNaN(n));
        return {
          id: s.id || "",
          submitted_at: fmtDt(s.created_at),
          earliest_transaction_at: dates.length ? fmtDt(new Date(Math.min(...dates)).toISOString()) : "",
          latest_transaction_at: dates.length ? fmtDt(new Date(Math.max(...dates)).toISOString()) : "",
          destination_names: [...new Set(dests.map((d) => d.destination_name).filter(Boolean))].join(", "),
          destination_types: [...new Set(dests.map((d) => d.destination_type).filter(Boolean))].join(", "),
          invoice_ids: [...new Set(dests.map((d) => d.invoice_id).filter(Boolean))].join(", "),
          shipment_source: s.shipment_source || "traced",
          total_shipped_kg: Number(Object.values(bd).reduce((a, b) => a + b, 0).toFixed(3)),
          ...shippedCols,
          admin_status: s.admin_status || "pending",
          rejection_reason: s.admin_status === "rejected" ? (s.rejection_reason || "") : "",
        };
      });
      downloadCsv(rows, `shipments_${Date.now()}.csv`);
    }
  }

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="recent-activity-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Activity log</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000]">
              Historical records
            </h3>
            <p className="text-xs text-[#3F3F3F]/70 mt-0.5">
              Received deliveries, processing runs and outbound shipments. Use filters to drill in.
            </p>
          </div>
          <div className="flex items-center gap-1 border border-[#D2E4D6] rounded-lg p-1 bg-white">
            {tabs.map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setTab(t.v)}
                data-testid={t.testId}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  tab === t.v
                    ? "gfs-bg-primary text-white"
                    : "text-[#3F3F3F]/70 hover:text-[#056839]"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 items-end mb-4 p-3 bg-[#F9F8F6] rounded-lg border border-[#D2E4D6]/50" data-testid="history-filters">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">From</label>
            <Input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              data-testid="history-from"
              className="h-9 mt-1 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">To</label>
            <Input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              data-testid="history-to"
              className="h-9 mt-1 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Material</label>
            <select
              value={filters.material_type}
              onChange={(e) => setFilters({ ...filters, material_type: e.target.value })}
              data-testid="history-material"
              className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
            >
              <option value="">All materials</option>
              {lookup.materials.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {showCollectorFilter && (
            <div className="flex-1 min-w-[160px]">
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Collector</label>
              <select
                value={filters.collector_id}
                onChange={(e) => setFilters({ ...filters, collector_id: e.target.value })}
                data-testid="history-collector"
                className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
              >
                <option value="">All collectors</option>
                {lookup.collectors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {showDestinationFilters && (
            <>
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Destination type</label>
                <select
                  value={filters.destination_type}
                  onChange={(e) => setFilters({ ...filters, destination_type: e.target.value })}
                  data-testid="history-dest-type"
                  className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                >
                  <option value="">All types</option>
                  {lookup.destination_types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Treatment partner</label>
                <select
                  value={filters.destination_name}
                  onChange={(e) => setFilters({ ...filters, destination_name: e.target.value })}
                  data-testid="history-dest-name"
                  className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                >
                  <option value="">All treatment partners</option>
                  {lookup.destinations.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Status</label>
                <select
                  value={shipmentStatusFilter}
                  onChange={(e) => setShipmentStatusFilter(e.target.value)}
                  data-testid="history-shipment-status"
                  className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </>
          )}
          <button
            type="button"
            onClick={() => { setFilters({ from: "", to: "", material_type: "", collector_id: "", destination_type: "", destination_name: "" }); setShipmentStatusFilter(""); }}
            data-testid="history-filters-reset"
            className="text-xs text-[#3F3F3F]/70 underline hover:text-[#056839] h-9 px-2"
          >
            Reset
          </button>
          <Button
            type="button"
            onClick={exportFiltered}
            data-testid="history-export-csv"
            className="h-9 gfs-bg-primary hover:bg-[#044E2A] text-white"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export filtered records
          </Button>
          <Button
            type="button"
            onClick={exportCompliancePackage}
            data-testid="history-export-zip"
            variant="outline"
            disabled={exportBusy}
            title="Download the evidence ZIP for the selected date range. Large ranges can take a moment."
            className="h-9 border-[#056839] text-[#056839] hover:bg-[#D2E4D6] disabled:opacity-60"
          >
            {exportBusy ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Preparing{exportBytes > 0 ? ` · ${fmtMB(exportBytes)}` : "…"}
              </>
            ) : (
              <>
                <Package className="w-4 h-4 mr-1.5" /> Compliance package
              </>
            )}
          </Button>
        </div>

        {tab === "received" && (
          <div className="overflow-x-auto" data-testid="history-received-table">
            {loading ? <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">Loading…</div> : collections.length === 0 ? (
              <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">No receipts match these filters.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#D2E4D6]/40">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Collector</th>
                    <th className="px-3 py-2">Material</th>
                    <th className="px-3 py-2 text-right">Weight (kg)</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setDetail({ kind: "received", data: c })}
                      data-testid={`history-received-row-${c.id}`}
                      className="border-t border-[#D2E4D6]/60 cursor-pointer hover:bg-[#F9F8F6]"
                    >
                      <td className="px-3 py-2 text-xs text-[#3F3F3F]">{fmtTs(c.date_received || c.timestamp)}</td>
                      <td className="px-3 py-2 text-[#000000]">
                        <div>{c.source_location || "—"}</div>
                        {c.reference_number && (
                          <div className="text-[10px] text-[#056839]/80 mt-0.5">
                            Ref: {c.reference_number}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[#3F3F3F]/80">
                        {c.collector_name || "—"}{idSuffix(c.collector_id)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
                          {c.material_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#056839] font-semibold">
                        {Number(c.received_weight_kg || 0).toFixed(1)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#3F3F3F]">
                        {c.transaction_amount != null ? (
                          <span className="inline-flex items-center gap-1.5">
                            {Number(c.transaction_amount).toFixed(2)} {c.transaction_currency || ""}
                            {c.payment_proof_path && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURI(c.payment_proof_path)}`}
                                target="_blank"
                                rel="noreferrer"
                    download
                                onClick={(e) => e.stopPropagation()}
                                className="text-[10px] uppercase tracking-wider text-[#056839] underline"
                              >
                                proof
                              </a>
                            )}
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "processed" && (
          <div className="overflow-x-auto" data-testid="history-processed-table">
            {loading ? <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">Loading…</div> : processings.length === 0 ? (
              <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">No processing runs match these filters.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#D2E4D6]/40">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Process</th>
                    <th className="px-3 py-2 text-right">Input (kg)</th>
                    <th className="px-3 py-2 text-right">Outputs</th>
                  </tr>
                </thead>
                <tbody>
                  {processings.map((p) => {
                    const outKg = (p.outputs || []).reduce((s, o) => s + (Number(o.weight_kg) || 0), 0);
                    return (
                      <tr
                        key={p.id}
                        onClick={() => setDetail({ kind: "processed", data: p })}
                        data-testid={`history-processed-row-${p.id}`}
                        className="border-t border-[#D2E4D6]/60 cursor-pointer hover:bg-[#F9F8F6]"
                      >
                        <td className="px-3 py-2 text-xs text-[#3F3F3F]">{fmtTs(p.processing_date)}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(p.processing_types || []).map((t) => (
                              <span key={t} className="px-2 py-0.5 rounded-md bg-[#E4D5B7]/60 text-[#A0522D] text-[10px] font-semibold uppercase tracking-wider">
                                {t}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[#3F3F3F]">
                          {Number(p.total_input_weight_kg || 0).toFixed(1)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[#056839] font-semibold">
                          {outKg.toFixed(1)} kg · {(p.outputs || []).length} batches
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "shipped" && (
          <div className="overflow-x-auto" data-testid="history-shipped-table">
            {loading ? <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">Loading…</div> : visibleShipments.length === 0 ? (
              <div className="text-sm text-[#3F3F3F]/60 py-6 text-center">No shipments match these filters.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#D2E4D6]/40">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Treatment partner</th>
                    <th className="px-3 py-2">Destination type</th>
                    <th className="px-3 py-2">Material</th>
                    <th className="px-3 py-2 text-right">Weight (kg)</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleShipments.flatMap((s) =>
                    (s.destinations || []).map((d, i) => (
                      <tr
                        key={`${s.id}-${i}`}
                        onClick={() => setDetail({ kind: "shipped", data: { shipment: s, destination: d }, type: "shipment", id: s.id, admin_status: s.admin_status })}
                        data-testid={`history-shipped-row-${s.id}-${i}`}
                        className="border-t border-[#D2E4D6]/60 cursor-pointer hover:bg-[#F9F8F6]"
                      >
                        <td className="px-3 py-2 text-xs text-[#3F3F3F]">{fmtTs(d.transaction_at)}</td>
                        <td className="px-3 py-2 text-[#000000]">{d.destination_name}</td>
                        <td className="px-3 py-2 text-[#3F3F3F]/80">{d.destination_type}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
                            {d.material_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[#056839] font-semibold">
                          {Number(d.weight_kg || 0).toFixed(1)}
                        </td>
                        <td className="px-3 py-2">
                          {s.admin_status === "rejected" && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Rejected by admin
                            </span>
                          )}
                          {s.admin_status === "approved" && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Approved
                            </span>
                          )}
                          {s.admin_status === "rejected" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/facility/shipment/${s.id}/edit`); }}
                              data-testid={`reedit-shipment-${s.id}`}
                              className="ml-2 px-2 py-1 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                              Re-edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>

      {/* Detail dialog for any history row */}
      <HistoryDetailDialog detail={detail} onClose={() => setDetail(null)} />

    </Card>
  );
}


/* ----------- Destinations directory ----------- */
function DestinationsTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, full obj for edit
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/destinations");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function startNew() {
    setEditing({ name: "", type: "Recycler", contact_person: "", phone: "", email: "", address: "", status: "Active" });
  }
  function startEdit(d) {
    setEditing({ ...d });
  }

  async function save() {
    if (!editing.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = {
        name: editing.name.trim(),
        type: editing.type,
        contact_person: (editing.contact_person || "").trim() || null,
        phone: (editing.phone || "").trim() || null,
        email: (editing.email || "").trim() || null,
        address: (editing.address || "").trim() || null,
        status: editing.status,
      };
      if (editing.id) {
        await api.patch(`/destinations/${editing.id}`, body);
      } else {
        await api.post("/destinations", body);
      }
      toast.success(editing.id ? "Destination updated" : "Destination added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="destinations-tab">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Directory</div>
          <h2 className="font-heading text-xl font-semibold text-[#000000]">
            Treatment partners
          </h2>
          <p className="text-sm text-[#3F3F3F]/70 mt-1">
            Saved recyclers, converters, exporters and disposal partners — used as dropdown choices in shipment forms.
          </p>
        </div>
        <Button
          onClick={startNew}
          data-testid="add-destination-btn"
          className="h-11 gfs-bg-primary hover:bg-[#044E2A] text-white rounded-lg"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add treatment partner
        </Button>
      </div>

      <Card className="border-[#D2E4D6]/80 rounded-xl">
        <CardContent className="p-0">
          <table className="w-full" data-testid="destinations-table">
            <thead className="bg-[#D2E4D6]/50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Destination type</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Phone / Email</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#3F3F3F]/60">
                    <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No destinations yet. Add your regular partners to speed up shipment logging.
                  </td>
                </tr>
              )}
              {list.map((d) => (
                <tr key={d.id} className="border-t border-[#D2E4D6]/60 hover:bg-[#F9F8F6]">
                  <td className="px-4 py-3 font-medium text-[#000000]">{d.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
                      {d.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#3F3F3F]">{d.contact_person || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#3F3F3F]/80">
                    {d.phone || ""}
                    {d.email && (
                      <div className="text-[#3F3F3F]/60">{d.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#3F3F3F]/80 max-w-[240px] line-clamp-2">
                    {d.address || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${
                        d.status === "Active"
                          ? "bg-[#D2E4D6] text-[#056839]"
                          : "bg-[#3F3F3F]/10 text-[#3F3F3F]"
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(d)}
                      data-testid={`edit-destination-${d.id}`}
                      className="text-[#056839] hover:bg-[#D2E4D6]"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="destination-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit treatment partner" : "Add treatment partner"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Name <span className="text-[#A0522D] normal-case">*</span>
                  </Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Paprec PET Recycling"
                    data-testid="dest-dir-name"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Destination type <span className="text-[#A0522D] normal-case">*</span>
                  </Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="dest-dir-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEST_DIR_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="dest-dir-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Contact person</Label>
                  <Input
                    value={editing.contact_person || ""}
                    onChange={(e) => setEditing({ ...editing, contact_person: e.target.value })}
                    data-testid="dest-dir-contact"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Phone</Label>
                  <Input
                    value={editing.phone || ""}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    data-testid="dest-dir-phone"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Email</Label>
                  <Input
                    type="email"
                    value={editing.email || ""}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    data-testid="dest-dir-email"
                    className="mt-1 h-11"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Address / location</Label>
                  <textarea
                    value={editing.address || ""}
                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                    rows={2}
                    data-testid="dest-dir-address"
                    className="mt-1 w-full rounded-lg border-2 border-[#E4D5B7] focus:border-[#056839] focus:outline-none focus:ring-2 focus:ring-[#056839]/15 px-3 py-2 text-sm bg-white"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="dest-dir-cancel">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="dest-dir-save"
              className="gfs-bg-primary hover:bg-[#044E2A] text-white"
            >
              {editing?.id ? "Save changes" : "Add treatment partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
